import { createChatSuggestions } from '@/pages/api/scribeAPIV2';
import { useMutation, useMutationState } from '@tanstack/react-query';

import type { ChatSuggestion } from '@/components/chat/types/types';

export const CHAT_SUGGESTIONS_MUTATION_KEY = ['chat-suggestions'] as const;

/**
 * Normalize suggestion to ChatSuggestion format.
 * Handles both string format (legacy API) and object format.
 */
export function normalizeSuggestion(suggestion: string | ChatSuggestion): ChatSuggestion {
  if (typeof suggestion === 'string') {
    return { content: suggestion, type: 'free' };
  }
  return suggestion;
}

export function useMutationChatSuggestions() {
  return useMutation({
    mutationKey: CHAT_SUGGESTIONS_MUTATION_KEY,
    gcTime: 0,
    mutationFn: async ({
      text,
      specialtyGroupId,
      sessionId,
    }: {
      text: string;
      specialtyGroupId?: string;
      /** Active session id for span tagging (heidi.session_id). See EVI-1540. */
      sessionId?: string | null;
    }) => {
      const { data } = await createChatSuggestions({
        text,
        specialty_group_id: specialtyGroupId,
        ...(sessionId ? { session_id: sessionId } : {}),
      });
      // Normalize suggestions to handle both string[] and ChatSuggestion[] formats
      const suggestions = (data.suggestions ?? []).map(normalizeSuggestion);
      return { suggestions };
    },
  });
}

/**
 * Hook to access the latest chat suggestions from the mutation cache.
 * Used by other hooks that need to check if input matches a suggestion.
 */
export function useChatSuggestionsData() {
  const mutations = useMutationState({
    filters: { mutationKey: CHAT_SUGGESTIONS_MUTATION_KEY },
    select: (mutation) =>
      mutation.state.data as { suggestions?: ChatSuggestion[] } | undefined,
  });

  // Get the most recent successful mutation's data
  const latestData = mutations.at(-1);
  return latestData?.suggestions ?? [];
}
